import { useSaveBillablePrice } from '@domains/subscription/priv/api/queries'
import { useCreateBillableTemplatePrice } from '@domains/subscription/priv/Billables/api/query'
import type { BillableFormPluginProps, PriceRowPlugin } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { CreatePricePlugin, CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import { MinPriceForm } from '@domains/subscription/priv/Billables/usecases/Price/min'
import { MinPriceRow } from '@domains/subscription/priv/Billables/usecases/Price/min/MinPriceRow'
import { isMin } from '@domains/subscription/priv/Billables/usecases/Price/min/minMapper'
import type { BaseAlgorithm, BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { CreatePriceRequest } from '@domains/subscription/priv/types'
import { type ComponentType, memo } from 'react'

const MinPriceFormFactory = (props: CreatePricePluginProps) => {
  const { mutate } = useCreateBillableTemplatePrice(props.billableId, props.templateId)

  const handleCreatePrice = (price: BasePrice) => {
    if (!isMin(price)) {
      throw new Error(`Attempted to create a non-min price with MinPriceFormFactory: ${price.type}`)
    }

    mutate(price, {
      onSuccess: () => {
        props.onCreatedPrice(price)
      },
    })
  }

  const getAlgorithmPlugin = (algorithm: BaseAlgorithm) => {
    const plugin = props.additionalPlugins?.find((plugin) => plugin.supports(algorithm.type))

    return plugin?.FormComponent
  }

  return <MinPriceForm {...props} getAlgorithmPlugin={getAlgorithmPlugin} onCreatedPrice={handleCreatePrice} />
}

const MinPriceFormComponent = (props: CreatePricePluginProps) => {
  const handleCreatePrice = (price: BasePrice) => {
    if (!isMin(price)) {
      throw new Error(`Attempted to create a non-min price with MinPriceFormFactory: ${price.type}`)
    }

    props.onCreatedPrice(price)
  }

  const getAlgorithmPlugin = (algorithm: BaseAlgorithm) => {
    const plugin = props.additionalPlugins?.find((plugin) => plugin.supports(algorithm.type))

    return plugin?.FormComponent
  }

  return <MinPriceForm {...props} onCreatedPrice={handleCreatePrice} getAlgorithmPlugin={getAlgorithmPlugin} />
}

export class MinCreatePricePlugin implements CreatePricePlugin {
  constructor(public additionalPlugins?: CreatePricePlugin[]) {}
  type = 'Min'
  supports(type): boolean {
    return type === 'Min'
  }
  Component: ComponentType<CreatePricePluginProps> = MinPriceFormFactory
  FormComponent: ComponentType<CreatePricePluginProps> = MinPriceFormComponent
}

const MinPriceRowFactory = memo((props: BillableFormPluginProps) => {
  const { mutateAsync: saveBillable } = useSaveBillablePrice(props.templateId)

  const handleSave = async (data: CreatePriceRequest) => {
    await saveBillable({
      billableId: data.billableId,
      billablePrice: data,
    })
  }

  if (!isMin(props.price)) {
    return null
  }

  return <MinPriceRow {...props} additionalPlugins={props.additionalPlugins} minPrice={props.price} onSubmit={handleSave} />
})

const MinPriceRowFactoryComponent = memo((props: BillableFormPluginProps) => {
  if (!isMin(props.price)) {
    return null
  }

  return <MinPriceRow {...props} minPrice={props.price} onSubmit={noopAsync} />
})

export class MinPriceRowPlugin implements PriceRowPlugin {
  constructor(public additionalPlugins: PriceRowPlugin[]) {}
  type = 'Min'

  supports(algorithm): boolean {
    return algorithm.type === this.type
  }

  Component: ComponentType<BillableFormPluginProps> = MinPriceRowFactory
  FormComponent: ComponentType<BillableFormPluginProps> | undefined = MinPriceRowFactoryComponent
}

const noopAsync = async () => {}
